import { CSSProperties, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Sidebar from "@rio-cloud/rio-uikit/Sidebar";
import moment from "moment";
import {
  getFuelingAlertHistory,
  getFuelingAlertDetail,
  AlertHistoryApiItem,
  AlertDetailResponse,
} from "@services/fueling";
import SimpleMap from "@components/SimpleMap";
import AlertsHistoryListSkeleton, {
  AlertsHistoryPaginationSkeleton,
} from "@components/Skeletons/AlertsHistoryListSkeleton";
import AlertsHistoryDetailSkeleton from "@components/Skeletons/AlertsHistoryDetailSkeleton";
import { useT } from "@hooks";

type AlertHistoryType = "abastecimento" | "consumo" | "valor";

export type AlertsHistoryFilterKey = "todos" | "abastecimento" | "consumo" | "valor";

const PAGE_SIZE = 10;

const RESET_LINHA_ALERTA: CSSProperties = {
  background: "none",
  borderStyle: "none",
  padding: 0,
  textAlign: "inherit",
  width: "100%",
};

const FALLBACK_COORDS = { lat: -15.7801, lng: -47.9292 };

const SEM_FUNDO: CSSProperties = { background: "none" };

const FILTER_TO_API_TYPES: Record<AlertsHistoryFilterKey, string[] | undefined> = {
  todos: undefined,
  abastecimento: ["ABOVE_TANK_CAPACITY"],
  consumo: ["ABNORMAL_CONSUMPTION"],
  valor: ["ABOVE_FLEET_AVG_PRICE"],
};

const API_TYPE_TO_LOCAL: Record<string, AlertHistoryType> = {
  ABOVE_TANK_CAPACITY: "abastecimento",
  ABNORMAL_CONSUMPTION: "consumo",
  ABOVE_FLEET_AVG_PRICE: "valor",
};

interface AlertsHistorySidebarProps {
  open: boolean;
  onClose: () => void;
  initialFilter?: AlertsHistoryFilterKey;
}

type TabKey = "historico" | "detalhes";

interface MappedItem {
  id: string;
  date: string;
  time: string;
  driver: string;
  plate: string;
  detail: string;
  type: AlertHistoryType;
  raw: AlertHistoryApiItem;
}

const mapApiItem = (item: AlertHistoryApiItem): MappedItem => {
  const occurredAt = item.occurredAt ? moment(item.occurredAt) : null;
  return {
    id: String(item.id),
    date: occurredAt?.format("DD/MM/YYYY") ?? "-",
    time: occurredAt?.format("HH:mm") ?? "-",
    driver: item.driverName || "-",
    plate: item.vehiclePlate || "-",
    detail: item.alertMessage || item.message || item.detail || "-",
    type: API_TYPE_TO_LOCAL[item.type] ?? "abastecimento",
    raw: item,
  };
};

const resolveMapCoords = (locationCoordinates: string | null) => {
  const parsedCoords = locationCoordinates?.split(",").map((value) => Number.parseFloat(value.trim())) ?? [];
  const hasValidCoords =
    parsedCoords.length === 2 && Number.isFinite(parsedCoords[0]) && Number.isFinite(parsedCoords[1]);
  return {
    lat: hasValidCoords ? parsedCoords[0] : FALLBACK_COORDS.lat,
    lng: hasValidCoords ? parsedCoords[1] : FALLBACK_COORDS.lng,
  };
};

interface AlertsHistoryItemsProps {
  isLoading: boolean;
  items: MappedItem[];
  onSelectAlert: (alertId: number) => void;
}

const AlertsHistoryItems = ({ isLoading, items, onSelectAlert }: Readonly<AlertsHistoryItemsProps>) => {
  const t = useT();

  if (isLoading) {
    return <AlertsHistoryListSkeleton />;
  }

  if (items.length === 0) {
    return (
      <div className="display-flex align-items-center justify-content-center padding-20">
        <span className="text-color-gray text-size-14">{t("sidebar.alerts.empty")}</span>
      </div>
    );
  }

  const chipLabels: Record<AlertHistoryType, string> = {
    abastecimento: t("fueling.alert.aboveTankCapacity"),
    consumo: t("fueling.alert.abnormalConsumption"),
    valor: t("fueling.alert.aboveFleetAvgPrice"),
  };

  return (
    <>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <button
            key={item.id}
            type="button"
            style={RESET_LINHA_ALERTA}
            tabIndex={0}
            onClick={() => {
              onSelectAlert(Number(item.id));
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelectAlert(Number(item.id));
              }
            }}
            className={`display-flex align-items-start justify-content-between gap-15 padding-y-15 padding-right-20 cursor-pointer ${
              isLast ? "" : "border border-style-solid border-bottom-only border-width-1 border-color-light"
            }`}
          >
            <div className="display-flex flex-column gap-5">
              <span className="text-size-14 text-medium">
                {item.date} · {item.time}
              </span>
              <span className="text-color-gray text-size-14">
                {item.driver} · {item.plate}
              </span>
              <span className="text-color-gray text-size-14">{item.detail}</span>
            </div>
            <span className="position-relative display-inline-block">
              {item.type === "abastecimento" && (
                <span
                  className="position-absolute top-0 left-0 width-100pct height-100pct rounded opacity-10 bg-map-marker-geofence"
                  aria-hidden="true"
                />
              )}
              <span
                className={`position-relative display-inline-block padding-x-10 padding-y-5 rounded text-size-12 text-bold ${
                  item.type === "abastecimento" ? "text-color-danger" : "bg-lightest"
                }`}
              >
                {chipLabels[item.type]}
              </span>
            </span>
          </button>
        );
      })}
    </>
  );
};

const AlertTypeBanner = ({ alertType }: Readonly<{ alertType: string }>) => {
  const t = useT();

  if (alertType === "ABOVE_TANK_CAPACITY") {
    return (
      <div className="position-relative display-flex align-items-center gap-10 padding-10 rounded border border-width-1 border-color-danger">
        <span
          className="position-absolute top-0 left-0 width-100pct height-100pct rounded opacity-10 bg-map-marker-geofence"
          aria-hidden="true"
        />
        <span className="position-relative rioglyph rioglyph-warning-sign text-color-danger text-size-18" />
        <span className="position-relative text-color-danger text-medium text-size-14">
          {t("sidebar.alerts.alertType.aboveTankCapacity")}
        </span>
      </div>
    );
  }

  if (alertType === "ABNORMAL_CONSUMPTION") {
    return (
      <div className="position-relative display-flex align-items-center gap-10 padding-10 rounded border border-width-1 border-color-warning">
        <span
          className="position-absolute top-0 left-0 width-100pct height-100pct rounded opacity-10 bg-warning"
          aria-hidden="true"
        />
        <span className="position-relative rioglyph rioglyph-warning-sign text-color-warning text-size-18" />
        <span className="position-relative text-color-warning text-medium text-size-14">
          {t("sidebar.alerts.alertType.abnormalConsumption")}
        </span>
      </div>
    );
  }

  return (
    <div className="position-relative display-flex align-items-center gap-10 padding-10 rounded border border-width-1 border-color-warning">
      <span
        className="position-absolute top-0 left-0 width-100pct height-100pct rounded opacity-10 bg-danger"
        aria-hidden="true"
      />
      <span className="position-relative rioglyph rioglyph-warning-sign text-color-warning text-size-18" />
      <span className="position-relative text-color-warning text-medium text-size-14">
        {t("sidebar.alerts.alertType.aboveFleetAvgPrice")}
      </span>
    </div>
  );
};

const AlertDataPanel = ({ detail }: Readonly<{ detail: AlertDetailResponse }>) => {
  const t = useT();

  if (detail.alertType === "ABOVE_TANK_CAPACITY") {
    return (
      <>
        <span className="display-block margin-top-20 text-color-gray text-size-14 text-medium">
          {t("sidebar.alerts.section.fuelingData")}
        </span>
        <div className="display-flex gap-10 margin-top-15">
          <div className="flex-1-1 display-flex flex-column align-items-center justify-content-center height-80 rounded border border-width-1 border-color-light">
            <span className="text-color-gray text-size-12">{t("sidebar.alerts.data.tankCapacity")}</span>
            <span className="text-size-20 text-medium">
              {detail.tankCapacity != null ? `${detail.tankCapacity.toLocaleString("pt-BR")} L` : "-"}
            </span>
          </div>
          <div className="position-relative flex-1-1 display-flex flex-column align-items-center justify-content-center height-80 rounded overflow-hidden border border-width-1 border-color-danger">
            <span
              className="position-absolute top-0 left-0 width-100pct height-100pct bg-map-marker-geofence opacity-10"
              aria-hidden="true"
            />
            <span className="position-relative text-color-danger text-size-12 text-medium">
              {t("sidebar.alerts.data.volumeRefueled")}
            </span>
            <span className="position-relative text-color-danger text-size-20 text-medium">
              {detail.volumeRefueled != null ? `${detail.volumeRefueled.toLocaleString("pt-BR")} L` : "-"}
            </span>
          </div>
        </div>
      </>
    );
  }

  if (detail.alertType === "ABNORMAL_CONSUMPTION") {
    return (
      <>
        <span className="display-block margin-top-20 text-color-gray text-size-14 text-medium">
          {t("sidebar.alerts.section.consumptionData")}
        </span>
        <div className="display-flex gap-10 margin-top-15">
          <div className="flex-1-1 display-flex flex-column align-items-center justify-content-center height-80 rounded border border-width-1 border-color-light">
            <span className="text-color-gray text-size-12">{t("sidebar.alerts.data.fleetAvgConsumption")}</span>
            <span className="text-size-20 text-medium">
              {detail.fleetAverageConsumption != null
                ? `${detail.fleetAverageConsumption.toLocaleString("pt-BR")} L`
                : "-"}
            </span>
          </div>
          <div className="position-relative flex-1-1 display-flex flex-column align-items-center justify-content-center height-80 rounded overflow-hidden border border-width-1 border-color-warning">
            <span
              className="position-absolute top-0 left-0 width-100pct height-100pct bg-map-marker-geofence opacity-10"
              aria-hidden="true"
            />
            <span className="position-relative text-color-warning text-size-12 text-medium">
              {t("sidebar.alerts.data.recordedConsumption")}
            </span>
            <span className="position-relative text-color-warning text-size-20 text-medium">
              {detail.recordedConsumption != null ? `${detail.recordedConsumption.toLocaleString("pt-BR")} L` : "-"}
            </span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <span className="display-block margin-top-20 text-color-gray text-size-14 text-medium">
        {t("fueling.alert.aboveFleetAvgPrice")}
      </span>
      <div className="display-flex gap-10 margin-top-15">
        <div className="flex-1-1 display-flex flex-column align-items-center justify-content-center height-80 rounded border border-width-1 border-color-light">
          <span className="text-color-gray text-size-12">{t("sidebar.alerts.data.avgPricePaid")}</span>
          <span className="text-size-20 text-medium">
            {detail.fleetAveragePricePerLiter != null
              ? `R$ ${detail.fleetAveragePricePerLiter.toFixed(2).replace(".", ",")}`
              : "-"}
          </span>
        </div>
        <div className="position-relative flex-1-1 display-flex flex-column align-items-center justify-content-center height-80 rounded overflow-hidden border border-width-1 border-color-warning">
          <span
            className="position-absolute top-0 left-0 width-100pct height-100pct bg-map-marker-geofence opacity-10"
            aria-hidden="true"
          />
          <span className="position-relative text-color-warning text-size-12 text-medium">
            {t("sidebar.alerts.data.pricePaid")}
          </span>
          <span className="position-relative text-color-warning text-size-20 text-medium">
            {detail.totalValue != null ? `R$ ${detail.totalValue.toFixed(2).replace(".", ",")}` : "-"}
          </span>
        </div>
      </div>
    </>
  );
};

const AlertMessageBanner = ({ alertType, message }: Readonly<{ alertType: string; message: string }>) => {
  if (alertType === "ABOVE_TANK_CAPACITY") {
    return (
      <div className="position-relative display-flex align-items-center gap-10 padding-10 margin-top-10 rounded border border-width-1 border-color-danger">
        <span
          className="position-absolute top-0 left-0 width-100pct height-100pct rounded opacity-10 bg-map-marker-geofence"
          aria-hidden="true"
        />
        <span className="position-relative rioglyph rioglyph-warning-sign text-color-danger text-size-18" />
        <span className="position-relative text-color-danger text-medium text-size-14">{message || "-"}</span>
      </div>
    );
  }

  return (
    <div className="position-relative display-flex align-items-center gap-10 padding-10 margin-top-10 rounded border border-width-1 border-color-warning">
      <span
        className="position-absolute top-0 left-0 width-100pct height-100pct rounded opacity-10 bg-map-marker-geofence"
        aria-hidden="true"
      />
      <span className="position-relative rioglyph rioglyph-warning-sign text-color-warning text-size-18" />
      <span className="position-relative text-color-warning text-medium text-size-14">{message || "-"}</span>
    </div>
  );
};

const AlertDetailContent = ({ detail }: Readonly<{ detail: AlertDetailResponse }>) => {
  const t = useT();
  const occurredAt = detail.occurredAt ? moment(detail.occurredAt) : null;
  const { lat, lng } = resolveMapCoords(detail.locationCoordinates);

  return (
    <>
      <AlertTypeBanner alertType={detail.alertType} />
      <span className="display-block margin-top-20 text-color-gray text-size-14 text-medium">
        {t("sidebar.alerts.section.location")}
      </span>
      <div className="margin-top-10 rounded overflow-hidden border border-width-1 border-color-light">
        <SimpleMap lat={lat} lng={lng} />
      </div>
      <hr className="margin-top-20 margin-bottom-0 border-color-light" />
      <span className="display-block margin-top-20 text-color-gray text-size-14 text-medium">
        {t("sidebar.alerts.section.identification")}
      </span>

      <div className="display-flex margin-top-15">
        <div className="width-50pct display-flex flex-column gap-5">
          <span
            className="text-color-gray text-size-12 text-medium text-uppercase"
            style={{ lineHeight: "15px", letterSpacing: 0 }}
          >
            {t("sidebar.alerts.data.driver")}
          </span>
          <span className="text-size-14 text-medium" style={{ lineHeight: "21px", letterSpacing: 0 }}>
            {detail.driverName || "-"}
          </span>
        </div>
        <div className="width-50pct display-flex flex-column gap-5">
          <span
            className="text-color-gray text-size-12 text-medium text-uppercase"
            style={{ lineHeight: "15px", letterSpacing: 0 }}
          >
            {t("sidebar.alerts.data.dateTime")}
          </span>
          <span className="text-size-14 text-medium" style={{ lineHeight: "21px", letterSpacing: 0 }}>
            {occurredAt ? `${occurredAt.format("DD/MM/YYYY")} · ${occurredAt.format("HH:mm")}` : "-"}
          </span>
        </div>
      </div>

      <hr className="margin-top-15 margin-bottom-0 border-color-light" />

      <div className="display-flex margin-top-15">
        <div className="width-50pct display-flex flex-column gap-5">
          <span
            className="text-color-gray text-size-12 text-medium text-uppercase"
            style={{ lineHeight: "15px", letterSpacing: 0 }}
          >
            {t("sidebar.alerts.data.plate")}
          </span>
          <span className="text-size-14 text-medium" style={{ lineHeight: "21px", letterSpacing: 0 }}>
            {detail.vehicle?.plate || "-"}
          </span>
        </div>
        <div className="width-50pct display-flex flex-column gap-5">
          <span
            className="text-color-gray text-size-12 text-medium text-uppercase"
            style={{ lineHeight: "15px", letterSpacing: 0 }}
          >
            {t("sidebar.alerts.data.model")}
          </span>
          <span className="text-size-14 text-medium" style={{ lineHeight: "21px", letterSpacing: 0 }}>
            {detail.vehicle?.model || "-"}
          </span>
        </div>
      </div>

      <hr className="margin-top-15 margin-bottom-0 border-color-light" />

      <div className="display-flex align-items-center justify-content-between margin-top-15">
        <div className="display-flex align-items-center gap-5">
          <span className="rioglyph rioglyph-filling-station text-color-primary text-size-16" />
          <span className="text-color-gray text-size-14">{t("sidebar.alerts.section.fuelType")}</span>
        </div>
        <span className="text-size-14 text-bold text-capitalize">{detail.fuelType?.toLowerCase() || "-"}</span>
      </div>

      <hr className="margin-top-15 margin-bottom-0 border-color-light" />

      <AlertDataPanel detail={detail} />
      <AlertMessageBanner alertType={detail.alertType} message={detail.message} />
    </>
  );
};

interface AlertDetailTabProps {
  isLoading: boolean;
  detail: AlertDetailResponse | null;
}

const AlertDetailTab = ({ isLoading, detail }: Readonly<AlertDetailTabProps>) => {
  const t = useT();

  if (isLoading) {
    return <AlertsHistoryDetailSkeleton />;
  }

  if (detail) {
    return <AlertDetailContent detail={detail} />;
  }

  return (
    <div className="flex-1-1 display-flex flex-column align-items-center justify-content-center text-center gap-10">
      <span className="text-color-gray text-size-20 text-medium">{t("sidebar.alerts.noAlertSelected")}</span>
      <span className="text-color-gray text-size-14 text-normal">{t("sidebar.alerts.noAlertSelectedDescription")}</span>
    </div>
  );
};

export default function AlertsHistorySidebar({
  open,
  onClose,
  initialFilter = "todos",
}: Readonly<AlertsHistorySidebarProps>) {
  const t = useT();

  const FILTER_HEADLINES: Record<AlertsHistoryFilterKey, string> = {
    todos: t("sidebar.alerts.filter.all"),
    abastecimento: t("fueling.alert.aboveTankCapacity"),
    consumo: t("fueling.alert.abnormalConsumption"),
    valor: t("fueling.alert.aboveFleetAvgPrice"),
  };

  const FILTERS: { key: AlertsHistoryFilterKey; label: string }[] = [
    { key: "todos", label: t("sidebar.alerts.filter.all") },
    { key: "abastecimento", label: t("sidebar.alerts.filter.fueling") },
    { key: "consumo", label: t("sidebar.alerts.filter.consumption") },
    { key: "valor", label: t("sidebar.alerts.filter.value") },
  ];

  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("historico");
  const [activeFilter, setActiveFilter] = useState<AlertsHistoryFilterKey>("todos");
  const [currentPage, setCurrentPage] = useState(1);
  const [items, setItems] = useState<MappedItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [alertDetail, setAlertDetail] = useState<AlertDetailResponse | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const filterHeadline = FILTER_HEADLINES[activeFilter];
  const detailFilterKey = alertDetail?.alertType ? API_TYPE_TO_LOCAL[alertDetail.alertType] : undefined;
  const detailHeadline = detailFilterKey ? FILTER_HEADLINES[detailFilterKey] : undefined;
  const activeHeadline = activeTab === "detalhes" && detailHeadline ? detailHeadline : filterHeadline;

  const fetchAlertDetail = useCallback(async (alertId: number) => {
    setIsLoadingDetail(true);
    setAlertDetail(null);
    setActiveTab("detalhes");
    try {
      const detail = await getFuelingAlertDetail(alertId);
      setAlertDetail(detail);
    } catch {
      setAlertDetail(null);
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  const fetchHistory = useCallback(async (filter: AlertsHistoryFilterKey, page: number) => {
    setIsLoading(true);
    try {
      const apiTypes = FILTER_TO_API_TYPES[filter];
      const response = await getFuelingAlertHistory(apiTypes, page - 1, PAGE_SIZE);
      setItems(response.content.map(mapApiItem));
      setTotalPages(Math.max(1, response.totalPages));
    } catch {
      setItems([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setContainer(document.getElementById("app-right-sidebar-slot") ?? document.body);
  }, []);

  useEffect(() => {
    if (open) {
      setActiveTab("historico");
      setActiveFilter(initialFilter);
      setCurrentPage(1);
      fetchHistory(initialFilter, 1);
    }
  }, [open, initialFilter, fetchHistory]);

  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      fetchHistory(activeFilter, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchHistory(activeFilter, newPage);
  };

  if (!container) return null;

  return createPortal(
    <Sidebar
      fly
      hasBackdrop
      makeBackdropVisible
      width={600}
      title={t("sidebar.alerts.title")}
      titleClassName="padding-left-10"
      position={Sidebar.RIGHT}
      closed={!open}
      onClose={onClose}
      onBackdropClick={onClose}
    >
      <div className="padding-x-20">{activeHeadline}</div>
      <div className="display-flex margin-top-15 border border-style-solid border-top-only border-width-1 border-color-light">
        <div className="width-20 border border-style-solid border-bottom-only border-width-2 border-color-light" />
        {(["historico", "detalhes"] as const).map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              tabIndex={0}
              onClick={() => setActiveTab(tab)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") setActiveTab(tab);
              }}
              style={SEM_FUNDO}
              className={`padding-y-10 padding-x-15 text-size-14 text-medium cursor-pointer border border-style-solid border-bottom-only border-width-2 user-select-none ${
                isActive ? "text-color-primary border-color-primary" : "text-color-gray border-color-light"
              }`}
            >
              {tab === "historico" ? t("sidebar.alerts.tab.history") : t("sidebar.alerts.tab.details")}
            </button>
          );
        })}
        <div className="flex-1-1 border border-style-solid border-bottom-only border-width-2 border-color-light" />
      </div>
      <div
        className={`padding-20 bg-highlight-decent ${
          activeTab === "detalhes" && !isLoadingDetail && !alertDetail ? "flex-1-1 display-flex flex-column" : ""
        }`}
      >
        {activeTab === "historico" ? (
          <>
            <div className="display-flex gap-10 flex-wrap">
              {FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  className={`btn btn-primary ${activeFilter === filter.key ? "" : "btn-outline"}`}
                  onClick={() => setActiveFilter(filter.key)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className="display-flex flex-column margin-top-20 overflow-y-auto" style={{ maxHeight: 560 }}>
              <AlertsHistoryItems isLoading={isLoading} items={items} onSelectAlert={fetchAlertDetail} />
            </div>
            {isLoading && <AlertsHistoryPaginationSkeleton />}
            {!isLoading && items.length > 0 && (
              <div className="display-flex justify-content-center margin-top-15">
                <div className="display-flex flex-column min-width-150">
                  <div className="display-flex align-items-center justify-content-center gap-10 margin-bottom-10">
                    <button
                      type="button"
                      style={SEM_FUNDO}
                      className="border-none padding-0 text-color-gray text-size-14 cursor-pointer"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    >
                      <span className="rioglyph rioglyph-chevron-left" aria-hidden="true" />
                    </button>
                    <span className="text-color-gray text-size-14">
                      {currentPage}/{totalPages}
                    </span>
                    <button
                      type="button"
                      style={SEM_FUNDO}
                      className="border-none padding-0 text-color-gray text-size-14 cursor-pointer"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    >
                      <span className="rioglyph rioglyph-chevron-right" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="progress height-3">
                    <div
                      className="progress-bar progress-bar-primary"
                      style={{ width: `${(currentPage / totalPages) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <AlertDetailTab isLoading={isLoadingDetail} detail={alertDetail} />
        )}
      </div>
    </Sidebar>,
    container
  );
}
